/**
 * Global, mounted-once patriotic flag background for AHP.
 *
 * The flag <video> is rendered a SINGLE time at the app root and persists across
 * route changes, so switching between Search and Scenarios never re-downloads or
 * re-decodes the clip (which previously caused a visible reload + a frame that
 * got stuck mid-tab-switch). Pages opt in by rendering <FlagBackground/> (a thin
 * activator) which ref-counts visibility through this provider.
 *
 * A watchdog re-kicks playback on pause/stall/visibility-change and on a slow
 * interval, guaranteeing the loop never freezes on a single frame in the iOS
 * WebView.
 *
 * The live tuner panel (title-tap 5× or ?bgtune=1) lives here too — one tuner
 * for the one shared video.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { useBrand } from "@/config/brands";

const LS_CFG = "ahp_scenario_bg_cfg"; // + _light / _dark
/** The city embed never shows the flag, and runs inside other sites' pages —
 *  so it must not download or autoplay the 1.4 MB clip either (it would sit
 *  invisible in the DOM). The embed is always a fresh load in its own frame,
 *  never a client-side navigation, so a path check at load is exact. */
const IN_EMBED = typeof window !== "undefined" && /^\/embed(\/|$)/.test(window.location.pathname);
const LS_DEBUG = "ahp_bg_debug";
const TUNE_EVENT = "ahp-bgtune";

export interface FlagBgCfg {
  flagOpacity: number;
  washOpacity: number;
  blur: number;
  saturate: number;
  blend: string;
}

const DEFAULTS_LIGHT: FlagBgCfg = { flagOpacity: 0.16, washOpacity: 0.64, blur: 0, saturate: 112, blend: "normal" };
const DEFAULTS_DARK: FlagBgCfg = { flagOpacity: 0.22, washOpacity: 0.7, blur: 0, saturate: 115, blend: "normal" };

const BLEND_MODES = ["normal", "multiply", "screen", "overlay", "soft-light", "hard-light", "lighten", "darken", "color-dodge", "color-burn"];

function loadCfg(dark: boolean): FlagBgCfg {
  const base = dark ? DEFAULTS_DARK : DEFAULTS_LIGHT;
  try {
    const raw = localStorage.getItem(LS_CFG + (dark ? "_dark" : "_light"));
    if (raw) return { ...base, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return base;
}

function readUrlFlag(): boolean {
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get("bgtune") === "1") localStorage.setItem(LS_DEBUG, "1");
    if (p.get("bgtune") === "0") localStorage.removeItem(LS_DEBUG);
    return localStorage.getItem(LS_DEBUG) === "1";
  } catch {
    return false;
  }
}

/**
 * Attach to a page title's onClick. Five taps within 1.5s toggles the tuner
 * panel — the in-app way to open it on a phone (no URL needed).
 */
let _taps: number[] = [];
export function tunerTap() {
  try {
    const now = Date.now();
    _taps = [..._taps.filter((t) => now - t < 1500), now];
    if (_taps.length >= 5) {
      _taps = [];
      const on = localStorage.getItem(LS_DEBUG) === "1";
      if (on) localStorage.removeItem(LS_DEBUG);
      else localStorage.setItem(LS_DEBUG, "1");
      window.dispatchEvent(new Event(TUNE_EVENT));
    }
  } catch { /* ignore */ }
}

// ── Ref-count context: pages register/unregister their desire for the flag ──
const FlagBgContext = createContext<{ acquire: () => void; release: () => void } | null>(null);

/** Call from a page to show the global flag background while that page is mounted. */
export function useFlagBackgroundActive() {
  const ctx = useContext(FlagBgContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.acquire();
    return () => ctx.release();
  }, [ctx]);
}

export function GlobalFlagBackgroundProvider({ children }: { children: React.ReactNode }) {
  const { isDark } = useTheme();
  const brand = useBrand();
  const isAhp = brand.key === "ahp";
  const [count, setCount] = useState(0);
  const [cfg, setCfg] = useState<FlagBgCfg>(DEFAULTS_LIGHT);
  const [debug, setDebug] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [copied, setCopied] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const active = count > 0;

  const acquire = useCallback(() => setCount((c) => c + 1), []);
  const release = useCallback(() => setCount((c) => Math.max(0, c - 1)), []);

  useEffect(() => {
    setCfg(loadCfg(isDark));
    setDebug(readUrlFlag());
    const onToggle = () => setDebug(localStorage.getItem(LS_DEBUG) === "1");
    window.addEventListener(TUNE_EVENT, onToggle);
    return () => window.removeEventListener(TUNE_EVENT, onToggle);
  }, [isDark]);

  // ── Watchdog: never let the loop freeze on a stuck frame ──────────────────
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const kick = () => { v.play().catch(() => { /* autoplay race; ignore */ }); };
    const onVisible = () => { if (document.visibilityState === "visible") kick(); };
    v.addEventListener("pause", kick);
    v.addEventListener("stalled", kick);
    v.addEventListener("suspend", kick);
    v.addEventListener("ended", kick); // belt-and-suspenders alongside loop attr
    document.addEventListener("visibilitychange", onVisible);
    let last = -1;
    const iv = window.setInterval(() => {
      if (!active) return;
      if (v.paused || v.currentTime === last) kick();
      last = v.currentTime;
    }, 3000);
    return () => {
      v.removeEventListener("pause", kick);
      v.removeEventListener("stalled", kick);
      v.removeEventListener("suspend", kick);
      v.removeEventListener("ended", kick);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(iv);
    };
  }, [active]);

  const update = useCallback((patch: Partial<FlagBgCfg>) => {
    setCfg((prev) => {
      const next = { ...prev, ...patch };
      try { localStorage.setItem(LS_CFG + (isDark ? "_dark" : "_light"), JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [isDark]);

  const reset = useCallback(() => {
    try { localStorage.removeItem(LS_CFG + (isDark ? "_dark" : "_light")); } catch { /* ignore */ }
    setCfg(isDark ? DEFAULTS_DARK : DEFAULTS_LIGHT);
  }, [isDark]);

  const washRgb = isDark ? "10,22,40" : "250,249,246";
  const wash = `linear-gradient(180deg, rgba(${washRgb},${cfg.washOpacity}) 0%, rgba(${washRgb},${Math.max(0, cfg.washOpacity - 0.12).toFixed(3)}) 48%, rgba(${washRgb},${cfg.washOpacity}) 100%)`;

  const panelBg = isDark ? "rgba(13,30,53,0.97)" : "rgba(255,255,255,0.97)";
  const panelBorder = isDark ? "rgba(197,165,90,0.35)" : "rgba(0,0,0,0.12)";
  const txt = isDark ? "#f0ebe3" : "#1B1B1B";
  const muted = isDark ? "rgba(240,235,227,0.6)" : "rgba(27,27,27,0.62)";
  const accent = isDark ? "#C5A55A" : "#B22234";

  const Slider = ({ label, val, min, max, step, fmt, onChange }: { label: string; val: number; min: number; max: number; step: number; fmt: (v: number) => string; onChange: (v: number) => void; }) => (
    <label className="block">
      <div className="flex items-center justify-between mb-0.5">
        <span style={{ color: muted }}>{label}</span>
        <span className="font-mono" style={{ color: txt }}>{fmt(val)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={val} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full" style={{ accentColor: accent }} />
    </label>
  );

  return (
    <FlagBgContext.Provider value={{ acquire, release }}>
      {/* Persistent video + wash — always in the DOM on AHP (so it never
          reloads), faded in/out by `active`. pointer-events none, behind all
          content. Skipped entirely on non-AHP brands to avoid loading the clip. */}
      {isAhp && !IN_EMBED && (
        <>
          <video
            ref={videoRef}
            autoPlay loop muted playsInline
            data-boot-critical="true"
            className="fixed inset-0 w-full h-full object-cover pointer-events-none"
            style={{
              opacity: active ? cfg.flagOpacity : 0,
              filter: `blur(${cfg.blur}px) saturate(${cfg.saturate}%)`,
              zIndex: 0,
              transition: "opacity 0.4s ease",
              visibility: active ? "visible" : "hidden",
            }}
          >
            <source src="/consumer/hero-flag-faded.mp4" type="video/mp4" />
          </video>
          <div
            className="fixed inset-0 pointer-events-none"
            style={{
              background: wash,
              mixBlendMode: cfg.blend as React.CSSProperties["mixBlendMode"],
              zIndex: 0,
              opacity: active ? 1 : 0,
              transition: "opacity 0.4s ease",
              visibility: active ? "visible" : "hidden",
            }}
          />
        </>
      )}

      {children}

      {debug && (
        <div
          className="fixed left-3 right-3 z-[10060] rounded-xl shadow-2xl"
          style={{ bottom: "calc(74px + env(safe-area-inset-bottom,0px))", backgroundColor: panelBg, border: `1px solid ${panelBorder}`, color: txt, backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", maxWidth: 420, marginLeft: "auto", marginRight: "auto", fontFamily: "'Instrument Sans', system-ui, sans-serif" }}
        >
          <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: collapsed ? "none" : `1px solid ${panelBorder}` }}>
            <div className="flex items-center gap-2 text-xs font-bold" style={{ color: accent }}>
              🎚 Background Tuner <span style={{ color: muted, fontWeight: 400 }}>{isDark ? "(dark)" : "(light)"}</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCollapsed((c) => !c)} className="px-2 py-1 rounded text-xs" style={{ color: muted }}>{collapsed ? "▲" : "▼"}</button>
              <button onClick={() => { try { localStorage.removeItem(LS_DEBUG); } catch { /* ignore */ } setDebug(false); }} className="px-2 py-1 rounded text-xs" style={{ color: muted }} title="Hide (tap the title 5× to reopen)">✕</button>
            </div>
          </div>

          {!collapsed && (
            <div className="px-3 py-2.5 space-y-2.5 text-[11px]">
              <Slider label="Flag opacity" val={cfg.flagOpacity} min={0} max={0.5} step={0.01} fmt={(v) => v.toFixed(2)} onChange={(v) => update({ flagOpacity: v })} />
              <Slider label="Wash opacity (readability)" val={cfg.washOpacity} min={0} max={1} step={0.02} fmt={(v) => v.toFixed(2)} onChange={(v) => update({ washOpacity: v })} />
              <Slider label="Blur" val={cfg.blur} min={0} max={10} step={0.5} fmt={(v) => `${v}px`} onChange={(v) => update({ blur: v })} />
              <Slider label="Saturation" val={cfg.saturate} min={40} max={220} step={5} fmt={(v) => `${v}%`} onChange={(v) => update({ saturate: v })} />
              <label className="block">
                <div className="mb-0.5" style={{ color: muted }}>Wash blend mode</div>
                <select value={cfg.blend} onChange={(e) => update({ blend: e.target.value })} className="w-full rounded px-2 py-1 text-xs" style={{ backgroundColor: isDark ? "#0a1628" : "#fff", color: txt, border: `1px solid ${panelBorder}` }}>
                  {BLEND_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => { try { navigator.clipboard?.writeText(JSON.stringify(cfg)); } catch { /* ignore */ } setCopied(true); setTimeout(() => setCopied(false), 1200); }}
                  className="flex-1 px-2 py-1.5 rounded text-xs font-semibold"
                  style={{ backgroundColor: accent, color: "#fff" }}
                >
                  {copied ? "Copied ✓" : "Copy values"}
                </button>
                <button onClick={reset} className="px-3 py-1.5 rounded text-xs font-semibold" style={{ border: `1px solid ${panelBorder}`, color: txt }}>Reset</button>
              </div>
              <div className="font-mono text-[9px] break-all" style={{ color: muted }}>{JSON.stringify(cfg)}</div>
            </div>
          )}
        </div>
      )}
    </FlagBgContext.Provider>
  );
}
